import React from 'react';
import SettingCard from './SettingCard';
import { panelLabel } from '../../utils/panelLabels';
import { useSettingsReadOnly } from './readOnlyContext';

// The panel keeps the moisture window, rate bias, the betas and the model
// readouts on one page, so they live together here too. The betas stay
// editable from this side, which the panel does not allow.
const ControlModelSetup = ({ getSettingValue, handleEdit }) => {
  const readOnly = useSettingsReadOnly();

  const modelSettings = [
    { name: 'maximum_moisture', label: 'Maximum Moisture' },
    { name: 'minimum_moisture', label: 'Minimum Moisture' }
  ];

  const betaSettings = [
    { name: 'model_beta_1', label: 'Model Beta 1' },
    { name: 'model_beta_2', label: 'Model Beta 2' }
  ];

  const rateBias = getSettingValue('rate_bias_enabled');
  const rateBiasEnabled = rateBias.value === true || rateBias.value === 'true' || rateBias.value === 1;

  // Zero from the controller means no suggestion (model not initialized or
  // dryer not running); the readout prints a dash for anything non-numeric.
  const suggestedRate = getSettingValue('suggested_rate').value;
  const suggestedRateOrNone = suggestedRate > 0 ? suggestedRate : null;

  const readout = (label, value, suffix) => (
    <div className="bg-white rounded-lg border border-gray-300 shadow-sm p-4">
      <div className="flex justify-between items-center">
        <span className="text-gray-700 font-medium capitalize">{label}</span>
        <span className="text-2xl font-bold text-gray-900">
          {typeof value === 'number' ? value.toFixed(2) : '--'}
          {suffix && <span className="text-lg ml-1">{suffix}</span>}
        </span>
      </div>
    </div>
  );

  const editableCard = (setting) => {
    const settingData = getSettingValue(setting.name);
    return (
      <SettingCard
        key={setting.name}
        name={panelLabel(setting.name, setting.label)}
        value={settingData.value}
        min={settingData.minRange}
        max={settingData.maxRange}
        onEdit={(_, value) => handleEdit(setting.name, value)}
      />
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        {modelSettings.map(editableCard)}

        {/* Rate bias on/off — boolean, so a toggle instead of a numeric card */}
        <div className="bg-white rounded-lg border border-gray-300 shadow-sm">
          <div className="p-4 pb-2">
            <p className="text-gray-700 font-medium text-center mb-3 border-b pb-2 capitalize">
              {panelLabel('rate_bias_enabled', 'Rate Bias Enabled')}
            </p>
            <p className={`text-3xl font-bold text-center mb-3 ${rateBiasEnabled ? 'text-green-600' : 'text-red-600'}`}>
              {rateBias.value === null ? '--' : rateBiasEnabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          {!readOnly && (
            <div className="flex">
              <button
                onClick={() => !rateBiasEnabled && handleEdit('rate_bias_enabled', true)}
                className={`flex-1 px-4 py-3 font-semibold border-r border-gray-300 transition-colors ${
                  rateBiasEnabled ? 'bg-[#2a4e7c] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enabled
              </button>
              <button
                onClick={() => rateBiasEnabled && handleEdit('rate_bias_enabled', false)}
                className={`flex-1 px-4 py-3 font-semibold transition-colors ${
                  !rateBiasEnabled ? 'bg-[#2a4e7c] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Disabled
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-t pt-6">
        <p className="text-sm text-gray-500 mb-4">
          The betas are the model&rsquo;s learned coefficients. The panel shows them read only;
          they are editable here.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {betaSettings.map(editableCard)}
        </div>
      </div>

      <div className="border-t pt-6">
        <div className="grid grid-cols-2 gap-4">
          {readout(panelLabel('predicted_moisture', 'Predicted Moisture'), getSettingValue('predicted_moisture').value, '%')}
          {readout(panelLabel('suggested_rate', 'Suggested Rate'), suggestedRateOrNone)}
        </div>
      </div>
    </div>
  );
};

export default ControlModelSetup;
